import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Event, EventOutput, EventsFilter, EventUpdate, Fleet } from '../interfaces';
import { setExpirationDateForGroups } from '../queries';
import { EventsService } from '../services';
import { deepCopy, hash } from '../utils';
import { useEventTypes } from './useEventTypes';
import { useFleet } from './useFleet';
import { useHasPermission } from './useHasPermission';

interface Props {
  filter: EventsFilter;
}

/**
 * Maximum number of events to keep in memory at once.
 * This prevents memory leaks by implementing a sliding window approach.
 * When the queue exceeds this limit, old events are pruned.
 */
const MAX_EVENTS_IN_MEMORY = 100;

/**
 * Buffer size for events to keep before and after the current index.
 * This ensures smooth navigation without immediately re-fetching events.
 */
const EVENTS_BUFFER_SIZE = 20;

export const useGenericEventsQueue = (
  props: Props,
  eventGetterFn: (filter: EventsFilter, fleet: Fleet, abortSignal?: AbortSignal) => Promise<EventOutput>,
) => {
  const { filter } = props;

  const { group_events } = filter;

  const [events, setEvents] = useState<Event[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [index, setIndex] = useState<number>(0);
  const fleet = useFleet();
  const eventTypes = useEventTypes();

  // Memoize the filter hash to prevent infinite loops
  const filterHash = useMemo(() => {
    const { cursor: _, ...filterWithoutCursor } = filter;
    return hash(filterWithoutCursor);
  }, [filter]);

  const current = events[index];
  const next = events[index + 1] || null;
  const prev = events[index - 1] || null;

  const abortController = useRef(new AbortController());

  /**
   * Tracks event IDs that have been pruned from the sliding window.
   * These IDs persist across pruning operations so they are still skipped
   * in future API calls, preventing re-fetching of already-seen events.
   */
  const prunedEventIds = useRef<Set<string>>(new Set());

  /**
   * Refs to track current values without triggering dependency changes.
   * This prevents unnecessary recreations of callbacks that depend on these values.
   */
  const indexRef = useRef(index);
  const eventsRef = useRef(events);

  const abortExistingRequest = useCallback(() => {
    abortController.current.abort();
    abortController.current = new AbortController();
  }, []);

  // Keep refs in sync with state
  useEffect(() => {
    indexRef.current = index;
    eventsRef.current = events;
  }, [index, events]);

  const resetQueue = useCallback(async () => {
    setLoading(true);
    setEvents([]);
    setCursor(null);
    setIndex(0);
    prunedEventIds.current.clear();
    abortExistingRequest();
    const res = await eventGetterFn({ ...filter, skipped_event_ids: [] }, fleet, abortController.current.signal);
    setEvents(res?.events ?? []);
    setCursor(res?.cursor ?? null);

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventGetterFn, filterHash, fleet, abortExistingRequest]);

  const loadMore = useCallback(async () => {
    setLoading(true);
    abortExistingRequest();

    // When using grouped review feature, when we're loading more events, we want to skip all the events
    // that are already in the queue, so we adjust the filter to include them in skipped_event_ids.
    const adjustedFilter = deepCopy(filter);

    const currentEvents = eventsRef.current;
    const eventIdsOfAllEventsInTheQueue = new Set(currentEvents.map((e) => e.id));
    currentEvents.forEach((e) => {
      e.sub_events?.forEach((se) => {
        eventIdsOfAllEventsInTheQueue.add(se.id);
      });
    });

    // Include both current queue events and previously pruned events in the skip list
    adjustedFilter.skipped_event_ids = [...(filter.skipped_event_ids ?? []), ...eventIdsOfAllEventsInTheQueue, ...prunedEventIds.current];

    const res = await eventGetterFn({ ...adjustedFilter, cursor }, fleet, abortController.current.signal);
    const newEventsOnly = (res?.events ?? []).filter((e) => !eventIdsOfAllEventsInTheQueue.has(e.id));

    let updatedEvents = [...currentEvents, ...newEventsOnly];
    let newIndex = indexRef.current;

    // Implement sliding window to prevent memory leaks
    // Keep only a reasonable number of events around the current index
    if (updatedEvents.length > MAX_EVENTS_IN_MEMORY) {
      const pruneFromIndex = Math.max(0, newIndex - EVENTS_BUFFER_SIZE);
      const pruneToIndex = Math.min(updatedEvents.length, pruneFromIndex + MAX_EVENTS_IN_MEMORY);

      // Track IDs of ALL events being pruned (both before and after the window)
      // so they're skipped in future API calls
      const eventsBeforeWindow = updatedEvents.slice(0, pruneFromIndex);
      // Events after the window exist when updatedEvents.length > pruneFromIndex + MAX_EVENTS_IN_MEMORY
      const eventsAfterWindow = updatedEvents.slice(pruneToIndex);

      eventsBeforeWindow.forEach((e) => {
        prunedEventIds.current.add(e.id);
        e.sub_events?.forEach((se) => {
          prunedEventIds.current.add(se.id);
        });
      });

      eventsAfterWindow.forEach((e) => {
        prunedEventIds.current.add(e.id);
        e.sub_events?.forEach((se) => {
          prunedEventIds.current.add(se.id);
        });
      });

      updatedEvents = updatedEvents.slice(pruneFromIndex, pruneToIndex);

      // Adjust index to account for pruned events
      newIndex -= pruneFromIndex;
    }

    setEvents(updatedEvents);
    setIndex(newIndex);
    if (res) {
      setCursor(res.cursor);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterHash, cursor, fleet, eventGetterFn, abortExistingRequest]);

  const gotoNext = useCallback(
    async (size = 1) => {
      const currentIndex = indexRef.current;
      const currentEventsLength = eventsRef.current.length;

      if (currentIndex + size >= currentEventsLength - 1) {
        await loadMore();
      }
      setIndex((prevIndex) => prevIndex + size);
    },
    [loadMore],
  );

  const updateEventInQueue = useCallback((newData: Partial<Event>) => {
    setEvents((currentEventsState) => {
      const eventId = newData.id;
      const eventToUpdateIndex = currentEventsState.findIndex((e) => e.id === eventId);

      // update all parent events
      const updatedEvents = currentEventsState.map((e, i) => deepCopy(i === eventToUpdateIndex ? { ...e, ...newData } : e));

      // update all sub-events across all parent events
      updatedEvents.forEach((ue) => {
        ue.sub_events?.forEach((se, i) => {
          if (se.id === eventId) {
            ue.sub_events[i] = deepCopy({ ...se, ...newData });
          }
        });
      });

      return updatedEvents;
    });
  }, []);

  const changeEventType = useCallback(
    (fields: Partial<EventUpdate>) => {
      const copiedEvent = deepCopy(current);
      const { event_type_id, event_type_identifier } = fields;
      const newEventType = eventTypes?.find((et) => et.identifier === event_type_identifier);

      const eventToChange = {
        ...copiedEvent,
        event_type_identifier,
        event_type_marker_color_hex: newEventType?.marker_color_hex,
        event_type_name: newEventType?.name,
      };

      if (group_events) {
        // For grouped events, we update the event type on the sub-events
        current.sub_events?.forEach((subEvent) => {
          EventsService.updateEvent(subEvent.id, { event_type_id }).then(() => updateEventInQueue({ ...eventToChange, id: subEvent.id }));
        });
      } else {
        // For non-grouped events, we update the event type directly on the parent
        EventsService.updateEvent(eventToChange.id, { event_type_id }).then(() => updateEventInQueue(eventToChange));
      }
    },
    [current, eventTypes, group_events, updateEventInQueue],
  );

  const externalEventUpdateHandler = useCallback(
    (e: any) => {
      const { detail } = e;
      updateEventInQueue(detail);
    },
    [updateEventInQueue],
  );

  useEffect(() => {
    resetQueue();
  }, [filterHash, fleet.id, resetQueue]);

  useEffect(() => {
    window.addEventListener('event-updated', externalEventUpdateHandler, false);

    return () => {
      window.removeEventListener('event-updated', externalEventUpdateHandler, false);
    };
  }, [externalEventUpdateHandler]);

  return { events, isLoading, current, next, index, gotoNext, updateEventInQueue, changeEventType, resetQueue, prev };
};

const getMyAssignedEvents = async (filter: EventsFilter, fleet: Fleet, abortSignal?: AbortSignal) => {
  let alreadyAssignedEvents = await EventsService.listGroupedEvents(abortSignal);

  if (alreadyAssignedEvents?.events.length < filter.limit) {
    alreadyAssignedEvents = await EventsService.makeGroupedEvents(filter, fleet, abortSignal);
  }

  if (alreadyAssignedEvents?.expires_at) {
    setExpirationDateForGroups(alreadyAssignedEvents.expires_at);
  }

  return alreadyAssignedEvents;
};

export const useEventsQueue = (props: Props) => {
  const isGrouping = props.filter.group_events;
  const isConcurrentReview = useHasPermission('events:write:assignments');

  if (isGrouping) {
    if (isConcurrentReview) {
      return useGenericEventsQueue(props, getMyAssignedEvents);
    }
    return useGenericEventsQueue(props, EventsService.getGroupedEvents);
  }
  return useGenericEventsQueue(props, EventsService.getEvents);
};

export const useAssetsQueue = (props: Props) => {
  return useGenericEventsQueue(props, EventsService.getAssets);
};
